/** Layout metadata + email-safe HTML for features-grid variants. */

import { escapeHtml } from './helpers';
import {
  hasFeatureIcon,
  renderFeatureIconHtml,
  type FeaturesGridLayoutVariant,
} from './featuresGridUtils';

export type FeaturesGridLayoutMeta = {
  titleAlign: 'left' | 'center';
  titleFontFamily: string;
  titleWeight: number;
  usesPackIcons: boolean;
};

export function getFeaturesGridLayoutMeta(variant: FeaturesGridLayoutVariant): FeaturesGridLayoutMeta {
  switch (variant) {
    case 'icon-2x2':
    case 'icon-list':
      return {
        titleAlign: 'left',
        titleFontFamily: "'Inter', 'system-ui', sans-serif",
        titleWeight: 700,
        usesPackIcons: true,
      };
    case 'numbered':
      return {
        titleAlign: 'center',
        titleFontFamily: "'Inter', 'system-ui', sans-serif",
        titleWeight: 700,
        usesPackIcons: true,
      };
    case 'compact-4':
      return {
        titleAlign: 'center',
        titleFontFamily: "'Inter', 'system-ui', sans-serif",
        titleWeight: 700,
        usesPackIcons: true,
      };
    case 'dark-band':
      return {
        titleAlign: 'center',
        titleFontFamily: "'Inter', 'system-ui', sans-serif",
        titleWeight: 700,
        usesPackIcons: true,
      };
    case 'cards':
    default:
      return {
        titleAlign: 'center',
        titleFontFamily: "'Playfair Display', serif",
        titleWeight: 850,
        usesPackIcons: false,
      };
  }
}

export function getFeaturesGridTitlePadding(
  variant: FeaturesGridLayoutVariant,
  isMobile: boolean
): string {
  if (variant === 'dark-band') {
    return isMobile ? '48px 24px 28px 24px' : '56px 40px 36px 40px';
  }
  if (variant === 'icon-2x2' || variant === 'icon-list') {
    return isMobile ? '48px 24px 28px 24px' : '64px 40px 32px 40px';
  }
  if (variant === 'compact-4' || variant === 'numbered') {
    return isMobile ? '44px 24px 28px 24px' : '56px 40px 32px 40px';
  }
  return isMobile ? '60px 24px 32px 24px' : '80px 40px 48px 40px';
}

export function getFeaturesGridBodyPadding(
  variant: FeaturesGridLayoutVariant,
  isMobile: boolean
): string {
  if (variant === 'dark-band') {
    return isMobile ? '0 24px 48px 24px' : '0 40px 56px 40px';
  }
  if (variant === 'icon-list') {
    return isMobile ? '0 24px 48px 24px' : '0 40px 64px 40px';
  }
  if (variant === 'icon-2x2') {
    return isMobile ? '0 24px 48px 24px' : '0 40px 64px 40px';
  }
  if (variant === 'compact-4' || variant === 'numbered') {
    return isMobile ? '0 24px 48px 24px' : '0 40px 64px 40px';
  }
  return isMobile ? '0 24px 60px 24px' : '0 40px 80px 40px';
}

function iconBoxHtml(feature: any, size: number, boxStyle: string): string {
  if (!hasFeatureIcon(feature)) return '';
  const iconHtml = renderFeatureIconHtml(feature, size);
  return `
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" style="${boxStyle}">
      <tr><td align="center" valign="middle">${iconHtml}</td></tr>
    </table>`;
}

export function renderFeaturesGridFeatureEmailHtml(
  feature: any,
  variant: FeaturesGridLayoutVariant,
  isMobile: boolean,
  index: number,
  options?: { textColor?: string; mutedColor?: string }
): string {
  const titleColor = options?.textColor || '#111827';
  const descColor = options?.mutedColor || '#374151';
  const title = feature.title
    ? `<div style="font-size: ${isMobile ? '16px' : '17px'}; font-weight: 700; color: ${titleColor}; margin-bottom: 6px; letter-spacing: -0.01em; line-height: 1.3;">${escapeHtml(feature.title)}</div>`
    : '';
  const desc = feature.description
    ? `<div style="font-size: ${isMobile ? '14px' : '15px'}; line-height: 1.55; color: ${descColor}; font-weight: 400;">${escapeHtml(feature.description)}</div>`
    : '';

  if (variant === 'icon-list') {
    const icon = iconBoxHtml(
      feature,
      26,
      'width: 48px; height: 48px; background-color: #FFFFFF; border-radius: 12px; border: 1px solid #E5E7EB; margin-right: 16px;'
    );
    return `
      <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="margin-bottom: ${isMobile ? '24px' : '28px'};">
        <tr>
          <td width="48" valign="top" style="width: 48px; padding-right: 16px; vertical-align: top;">${icon}</td>
          <td valign="top" style="vertical-align: top; text-align: left;">${title}${desc}</td>
        </tr>
      </table>`;
  }

  if (variant === 'numbered') {
    const num = String(index + 1).padStart(2, '0');
    const stepVisual = hasFeatureIcon(feature)
      ? `${iconBoxHtml(
          feature,
          28,
          'width: 56px; height: 56px; background-color: #FFFFFF; border-radius: 12px; border: 1px solid #E5E7EB;'
        )}
        <div style="font-size: 11px; font-weight: 700; color: #6B7280; margin-top: 6px;">Step ${num}</div>`
      : `<div style="font-size: ${isMobile ? '32px' : '40px'}; font-weight: 800; color: #E5E7EB; line-height: 1; letter-spacing: -0.03em;">${num}</div>`;
    return `
      <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="margin-bottom: ${isMobile ? '28px' : '0'};">
        <tr>
          <td valign="top" style="padding-bottom: 12px; text-align: left;">
            ${stepVisual}
          </td>
        </tr>
        <tr><td valign="top">${title}${desc}</td></tr>
      </table>`;
  }

  if (variant === 'compact-4') {
    const icon = iconBoxHtml(
      feature,
      22,
      'width: 44px; height: 44px; margin: 0 auto 14px auto; background-color: #F9FAFB; border-radius: 50%; border: 1px solid #E5E7EB;'
    );
    return `
      <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
        <tr>
          <td align="center" style="text-align: center; padding: ${isMobile ? '8px 6px' : '0 10px'};">
            ${icon}
            ${feature.title ? `<div style="font-size: ${isMobile ? '14px' : '15px'}; font-weight: 700; color: ${titleColor}; margin-bottom: 6px; line-height: 1.3;">${escapeHtml(feature.title)}</div>` : ''}
            ${feature.description ? `<div style="font-size: ${isMobile ? '12px' : '13px'}; line-height: 1.5; color: ${descColor};">${escapeHtml(feature.description)}</div>` : ''}
          </td>
        </tr>
      </table>`;
  }

  if (variant === 'dark-band') {
    const icon = iconBoxHtml(
      feature,
      26,
      'width: 52px; height: 52px; margin: 0 auto 16px auto; background-color: #1F2937; border-radius: 14px; border: 1px solid #374151;'
    );
    return `
      <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color: #1F2937; border-radius: 16px; border: 1px solid #374151; margin-bottom: 16px;">
        <tr>
          <td style="padding: ${isMobile ? '28px 20px' : '32px 24px'}; text-align: center;">
            ${icon}
            ${feature.title ? `<div style="font-size: ${isMobile ? '17px' : '18px'}; font-weight: 700; color: #F9FAFB; margin-bottom: 8px;">${escapeHtml(feature.title)}</div>` : ''}
            ${feature.description ? `<div style="font-size: ${isMobile ? '14px' : '15px'}; line-height: 1.55; color: #9CA3AF;">${escapeHtml(feature.description)}</div>` : ''}
          </td>
        </tr>
      </table>`;
  }

  if (variant === 'icon-2x2') {
    const icon = iconBoxHtml(
      feature,
      28,
      'margin-bottom: 16px; width: 56px; height: 56px; background-color: #FFFFFF; border-radius: 12px; border: 1px solid #E5E7EB;'
    );
    return `
      <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
        <tr><td align="left" valign="top" style="text-align: left;">${icon}${title}${desc}</td></tr>
      </table>`;
  }

  // cards
  const icon = iconBoxHtml(
    feature,
    32,
    'margin: 0 auto 20px auto; background-color: #FFFFFF; border-radius: 16px; box-shadow: 0 10px 15px -3px rgba(0,0,0,0.05); width: 64px; height: 64px;'
  );
  return `
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color: #F9FAFB; border-radius: 24px; border: 1px solid #F1F5F9; margin-bottom: 24px;">
      <tr>
        <td style="padding: ${isMobile ? '32px 20px' : '40px 20px'}; text-align: center;">
          ${icon}
          ${feature.title ? `<div style="font-size: ${isMobile ? '18px' : '20px'}; font-weight: 800; color: #111827; margin-bottom: 12px; letter-spacing: -0.01em;">${escapeHtml(feature.title)}</div>` : ''}
          ${feature.description ? `<div style="font-size: ${isMobile ? '14px' : '15px'}; line-height: 1.6; color: #64748B;">${escapeHtml(feature.description)}</div>` : ''}
        </td>
      </tr>
    </table>`;
}
